import HotpleLogo from "@/components/hotple-logo";
import SearchInput from "@/components/search-input";
import {
  MOBILE_SEARCH_ICON_CLASS,
  MobileSearchFieldShell,
  MobileSearchFrame,
  MobileSearchSideSlot,
} from "@/components/search-input/mobile-search-frame";
import Button from "@/components/ui/Buttons/button";
import Div from "@/components/ui/div";
import DropdownMenu from "@/components/ui/DropdownMenu";
import BgIcon from "@/components/ui/icons/bg-icon";
import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import { useGlobalContext } from "@/context/global/GlobalContext";
import FeedFilterDropdown from "../ul-sidebar/FeedFilterDropdown";
import { Suspense, useState, useEffect } from "react";
import { useTranslations } from "@/lib/static-intl";
import { useRouter } from "next/navigation";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { getCurrentAuthUser } from "@/api/auth";
import { getUserDataByUserId } from "@/api/user";
import useEffectivePathname from "@/hooks/useEffectivePathname";
import {
  ROUTES,
  buildCurrentUserProfilePath,
  isCompactUserLayoutPath,
  isCurrentPlayFeedPath,
} from "@/constants/routes";

const UL_HeaderUnprotected = ({
  handleFeedbackModalOpen,
  handlePlaceRegisterModalOpen,
}) => {
  const t = useTranslations("header");
  const pathname = useEffectivePathname();
  const {
    user,
    isLoading,
    setIsLoginModalOpen,
    totalUnreadNotifications,
    totalUnreadMessageCount,
  } = useAuthContext();
  const [activeSearchBar, setActiveSearchBar] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const router = useRouter();
  const { setSearch, setSearchInFocus, setFilter, setShowDropdown } =
    useGlobalContext();
  const isFeedPage = isCurrentPlayFeedPath(pathname);
  // 프로필은 공개 프로필 접두사(/@)로 판별한다.
  const isProfilePage = Boolean(pathname?.startsWith("/@"));
  // 검색 화면에는 페이지 자체 검색줄이 있다. 전역 검색 바까지 얹으면
  // 같은 기능이 위아래로 두 번 겹치므로 모바일에서는 숨긴다(2026-07-31 검수 2-A).
  const isSearchPage = pathname === ROUTES.current.search;
  // 알림·메시지·올리기도 화면 목적이 분명한데 맨 위를 전역 검색창이 차지하고 있었다
  // (프로필은 07-31b 에 이미 전용 상단바로 바꿨다). 같은 방식으로 맞춘다.
  const simpleTitleHeader = (() => {
    if (pathname?.startsWith("/notify")) return { title: "알림", showSearch: true };
    if (pathname?.startsWith("/messages")) return { title: "메시지", showSearch: true };
    if (
      pathname?.startsWith("/creator/upload")
    )
      return { title: "올리기", showBack: true };
    return null;
  })();
  const isCompactHeaderPage = isCompactUserLayoutPath(pathname);
  const currentUserProfilePath = buildCurrentUserProfilePath(
    user?.username,
    user?.userId
  );
  const isOwnProfilePage = Boolean(
    currentUserProfilePath && pathname === currentUserProfilePath
  );
  const isAuthenticated = Boolean(user?.userId);

  // Handle hydration to prevent SSR/client mismatch
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const routeProtected = (target) => {
    if (!user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }
    if (target) router.push(target);
  };

  const routeCurrentUserProfile = async () => {
    if (!user?.userId) {
      setIsLoginModalOpen(true);
      return;
    }

    let target = buildCurrentUserProfilePath(null, user.userId);

    try {
      const currentAuth = await getCurrentAuthUser();
      if (currentAuth?.token) {
        localStorage.setItem("jwtToken", currentAuth.token);
      }

      const latestUser = currentAuth?.user;
      target =
        buildCurrentUserProfilePath(
          latestUser?.username,
          latestUser?.userId || latestUser?.id || user.userId
        ) || target;
    } catch {
      try {
        const latestUser = await getUserDataByUserId(user.userId, { page: 1 });
        target =
          buildCurrentUserProfilePath(
            latestUser?.username,
            latestUser?.userId || latestUser?.id || user.userId
          ) || target;
      } catch {
        target = buildCurrentUserProfilePath(null, user.userId);
      }
    }

    router.push(target);
  };

  // 프로필 화면 전용 상단바 (2026-07-31 검수 6-A). 프로필을 보는 중에도
  // 화면 최상단을 검색창이 차지하던 것을 화면 목적에 맞는 제목으로 바꾼다.
  // 검색은 완전히 없애지 않고 작은 아이콘으로 남긴다(다른 탭에는 검색이 있다).
  const mobileProfileHeaderContent = (
    <div className="flex w-full items-center gap-1 md:hidden">
      {/* 제목 크기도 공용 상단바와 같게(디자인 기준 v2 · t-section) */}
      <span className="flex-1 truncate text-t-section tracking-[-0.02em] text-black dark:text-white">
        프로필
      </span>
      <button
        type="button"
        aria-label="검색"
        onClick={() => setActiveSearchBar(true)}
        className="grid h-9 w-9 place-items-center rounded-full text-black/55 dark:text-white/55"
      >
        <Iconify icon={"lucide:search"} className={"!h-5 !w-5 !text-current"} />
      </button>
      {isOwnProfilePage && (
        <button
          type="button"
          aria-label="프로필 설정"
          onClick={() => router.push("/creator/profile-settings")}
          className="grid h-9 w-9 place-items-center rounded-full text-black/55 dark:text-white/55"
        >
          <Iconify icon={"lucide:settings"} className={"!h-5 !w-5 !text-current"} />
        </button>
      )}
    </div>
  );

  const renderMobileTitleHeader = ({ title, showBack = false, showSearch = false }) => (
    <div className="flex w-full items-center gap-1 md:hidden">
      {showBack && (
        <button
          type="button"
          aria-label="뒤로"
          onClick={() => router.back()}
          className="grid h-9 w-9 place-items-center rounded-full text-black dark:text-white"
        >
          <Iconify icon={"formkit:left"} className={"!h-5 !w-5 !text-current"} />
        </button>
      )}
      <span className="flex-1 truncate text-[15px] font-extrabold tracking-[-0.02em] text-black dark:text-white">
        {title}
      </span>
      {showSearch && (
        <button
          type="button"
          aria-label="검색"
          onClick={() => setActiveSearchBar(true)}
          className="grid h-9 w-9 place-items-center rounded-full text-black/55 dark:text-white/55"
        >
          <Iconify icon={"lucide:search"} className={"!h-5 !w-5 !text-current"} />
        </button>
      )}
    </div>
  );

  const mobileMainHeaderContent = (
    <>
      {!activeSearchBar && (
        <MobileSearchFrame className="w-full md:hidden">
          <MobileSearchSideSlot
            as="button"
            type="button"
            aria-label="Home"
            className="relative overflow-visible"
            onClick={() => {
              router.push(ROUTES.current.play);
              setFilter("all");
              setSearch("");
              setSearchInFocus(false);
            }}
          >
            <img
              src="/icons/main/logo.svg"
              alt=""
              className="absolute left-1/2 top-1/2 h-[76px] w-[76px] max-w-none -translate-x-1/2 -translate-y-1/2 object-contain"
            />
          </MobileSearchSideSlot>

          <MobileSearchFieldShell
            as="button"
            type="button"
            aria-label="Search"
            className="flex items-center justify-end px-4 transition active:scale-[0.99]"
            onClick={() => setActiveSearchBar(true)}
          >
            <Iconify
              icon="lucide:search"
              className={`${MOBILE_SEARCH_ICON_CLASS} text-black dark:text-white`}
            />
          </MobileSearchFieldShell>

          <MobileSearchSideSlot
            as="button"
            type="button"
            aria-label="Map"
            className="text-black dark:text-white"
            onClick={() => router.push(ROUTES.current.place)}
          >
            <span
              className="block h-[29px] w-[29px] bg-current"
              aria-hidden="true"
              style={{
                WebkitMask:
                  "url(/icons/main/mappinned.svg) center / contain no-repeat",
                mask: "url(/icons/main/mappinned.svg) center / contain no-repeat",
              }}
            />
          </MobileSearchSideSlot>
        </MobileSearchFrame>
      )}

      {activeSearchBar && (
        <SearchInput
          mobileOverlayOnly
          autoOpenMobile
          mobileOverlayVariant="main"
          onMobileOverlayClose={() => setActiveSearchBar(false)}
        />
      )}
    </>
  );

  if (isCompactHeaderPage) {
    return (
      <>
        {isFeedPage && (
          <Div className="sticky top-0 left-0 z-40 flex items-center justify-between gap-4 border-b border-neutral-100 !bg-white !px-3 !py-3 md:hidden dark:border-neutral-800 dark:!bg-custom_black">
            {mobileMainHeaderContent}
          </Div>
        )}

        <Div className="sticky top-0 left-0 z-40 hidden h-[76px] items-center gap-5 bg-dark_primary_label/95 px-2 backdrop-blur md:flex lg:px-4 dark:bg-custom_black/95">
          <div className="min-w-0 flex-1">
            <SearchInput />
          </div>
          <div className="hidden shrink-0 items-center gap-1.5 lg:flex">
            <DropdownMenu
              className="!bottom-auto !right-0 !top-10 !w-60"
              handleFeedbackModalOpen={handleFeedbackModalOpen}
              handlePlaceRegisterModalOpen={handlePlaceRegisterModalOpen}
            >
              <BgIcon
                icon={"fluent:navigation-24-filled"}
                className={"!h-9 !w-9"}
                IconclassName={"!h-5 !w-5"}
              />
            </DropdownMenu>
            {!isHydrated || isLoading ? (
              <PlayHeaderAuthPlaceholder />
            ) : isAuthenticated ? (
              <>
                <PlayHeaderAction
                  icon="ri:chat-ai-line"
                  label="메시지"
                  badge={totalUnreadMessageCount}
                  onClick={() => routeProtected(ROUTES.user.messages)}
                />
                <PlayHeaderAction
                  icon="ci:bell"
                  label="알림"
                  badge={totalUnreadNotifications}
                  onClick={() => routeProtected(ROUTES.user.notifications)}
                />
                <button
                  type="button"
                  aria-label="프로필"
                  className="flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-light_bg_grey dark:hover:bg-brown"
                  onClick={routeCurrentUserProfile}
                >
                  <PostAvatar
                    src={user?.profilePicture}
                    className="!h-7 !w-7"
                  />
                </button>
              </>
            ) : (
              <button
                type="button"
                aria-label="로그인"
                className="flex h-9 items-center gap-2 rounded-full px-2.5 text-[15px] font-semibold text-light_primary_label transition hover:bg-light_bg_grey dark:text-dark_primary_label dark:hover:bg-brown"
                onClick={() => setIsLoginModalOpen(true)}
              >
                <span
                  className="block h-6 w-6 bg-current"
                  aria-hidden="true"
                  style={{
                    WebkitMask:
                      "url(/icons/play/user-round.svg) center / contain no-repeat",
                    mask: "url(/icons/play/user-round.svg) center / contain no-repeat",
                  }}
                />
                <span className="leading-none">로그인</span>
              </button>
            )}
          </div>
        </Div>
      </>
    );
  }

  return (
    <Div
      className={`sticky top-0 left-0 z-40 flex items-center justify-between gap-4 border-b border-neutral-100 !bg-white !px-3 !py-3 dark:border-neutral-800 dark:!bg-custom_black${
        isSearchPage && !activeSearchBar ? " max-md:hidden" : ""
      }`}
    >
      {simpleTitleHeader && !activeSearchBar
        ? renderMobileTitleHeader(simpleTitleHeader)
        : isProfilePage && !activeSearchBar
          ? mobileProfileHeaderContent
          : mobileMainHeaderContent}

      <div className="hidden w-full md:block lg:w-[500px]">
        <SearchInput />
      </div>

      <div className="hidden md:flex lg:hidden">
        {!activeSearchBar && (
          <>
            <BgIcon
              icon={"lucide:search"}
              className={"flex sm:hidden"}
              onClick={() => setActiveSearchBar(true)}
            />
            <DropdownMenu
              className={
                "!top-10 h-min !-ml-52 !w-60 sm:!w-64 !overflow-hidden block"
              }
              handleFeedbackModalOpen={handleFeedbackModalOpen}
            >
              <BgIcon icon={"fluent:navigation-24-filled"} />
            </DropdownMenu>
          </>
        )}

        {activeSearchBar && (
          <Button
            className={"!text-nowrap whitespace-nowrap"}
            onClick={() => setActiveSearchBar(false)}
          >
            {t("cancel")}
          </Button>
        )}
      </div>
    </Div>
  );
};

export default function UL_Header({
  handleFeedbackModalOpen,
  handlePlaceRegisterModalOpen,
}) {
  return (
    <Suspense fallback={<></>}>
      <UL_HeaderUnprotected
        handleFeedbackModalOpen={handleFeedbackModalOpen}
        handlePlaceRegisterModalOpen={handlePlaceRegisterModalOpen}
      />
    </Suspense>
  );
}

const PlayHeaderAction = ({ icon, label, badge, onClick }) => (
  <button
    type="button"
    aria-label={label}
    className="relative flex h-9 w-9 items-center justify-center rounded-full text-light_secondary_label transition hover:bg-light_bg_grey hover:text-light_primary_label dark:text-dark_secondary_label dark:hover:bg-brown dark:hover:text-dark_primary_label"
    onClick={onClick}
  >
    {!!badge && badge > 0 && (
      <span className="absolute right-0.5 top-0.5 z-10 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-red px-1 text-[11px] font-semibold leading-none text-white">
        {badge > 99 ? "99+" : badge}
      </span>
    )}
    <Iconify icon={icon} className="!h-5 !w-5" />
  </button>
);

const PlayHeaderAuthPlaceholder = () => (
  <>
    <span
      className="h-9 w-9 rounded-full bg-light_bg_grey dark:bg-brown"
      aria-hidden="true"
    />
    <span
      className="h-9 w-9 rounded-full bg-light_bg_grey dark:bg-brown"
      aria-hidden="true"
    />
    <span
      className="h-9 w-9 rounded-full bg-light_bg_grey dark:bg-brown"
      aria-hidden="true"
    />
  </>
);
